import cn from 'classnames';
import * as React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';

import styles from './styles.module.scss';

export interface AccordionProps {
  className?: string;
}

const Accordion: React.FC<AccordionProps> = ({ className, ...rest }) => {
  const ref = useRef<HTMLDivElement>(null);
  const [isEnhanced, setIsEnhanced] = useState(false);

  const ensurePanelReaderTitle = useCallback((header: HTMLButtonElement, content: HTMLElement) => {
    const titleElement = header.querySelector<HTMLElement>('.erom-accordion-item-title');
    const titleText = titleElement?.textContent?.trim();
    if (!titleText) return;

    const headerId = header.getAttribute('id');
    if (headerId) {
      content.setAttribute('aria-labelledby', headerId);
    }

    const existingTitle = content.querySelector<HTMLElement>('[data-accordion-reader-title="true"]');
    if (existingTitle) {
      existingTitle.textContent = titleText;
      return;
    }

    const readerTitle = document.createElement('h3');
    readerTitle.textContent = titleText;
    readerTitle.setAttribute('data-accordion-reader-title', 'true');
    readerTitle.className = styles.accordionReaderTitle;
    content.insertAdjacentElement('afterbegin', readerTitle);
  }, []);

  const syncAccordionContainer = useCallback(
    (container: ParentNode, expandedHeader?: HTMLButtonElement | null) => {
      const allHeaders = Array.from(container.querySelectorAll<HTMLButtonElement>('button.erom-accordion-item-header'));
      const allContents = Array.from(container.querySelectorAll<HTMLElement>('.erom-accordion-item-content'));

      const nextExpandedHeader =
        expandedHeader === undefined
          ? (allHeaders.find(header => header.getAttribute('aria-expanded') === 'true') ?? allHeaders[0])
          : expandedHeader;

      allHeaders.forEach(header => {
        const isExpanded = header === nextExpandedHeader;
        header.setAttribute('aria-expanded', isExpanded ? 'true' : 'false');
        const contentId = header.getAttribute('aria-controls');
        const content = contentId ? (container.querySelector(`#${contentId}`) as HTMLElement | null) : null;
        if (!content) return;

        ensurePanelReaderTitle(header, content);

        if (isExpanded) {
          content.removeAttribute('hidden');
          content.removeAttribute('aria-hidden');
          content.setAttribute('data-accordion-open', 'true');
        } else {
          content.removeAttribute('hidden');
          content.removeAttribute('aria-hidden');
          content.setAttribute('data-accordion-open', 'false');
        }
      });

      // Keep orphaned panels collapsed to avoid conflicting visibility states.
      allContents.forEach(content => {
        const controlledBy = allHeaders.some(header => header.getAttribute('aria-controls') === content.id);
        if (!controlledBy) {
          content.removeAttribute('hidden');
          content.removeAttribute('aria-hidden');
          content.setAttribute('data-accordion-open', 'false');
        }
      });
    },
    [ensurePanelReaderTitle]
  );

  useEffect(() => {
    setIsEnhanced(true);

    const root = ref.current;
    if (!root) return;

    const allContents = Array.from(root.querySelectorAll<HTMLElement>('.erom-accordion-item-content'));
    allContents.forEach(content => {
      content.removeAttribute('hidden');
      content.removeAttribute('aria-hidden');
      if (!content.hasAttribute('data-accordion-open')) {
        content.setAttribute('data-accordion-open', 'false');
      }

      const contentId = content.getAttribute('id');
      if (!contentId) return;

      const header = root.querySelector<HTMLButtonElement>(
        `button.erom-accordion-item-header[aria-controls="${contentId}"]`
      );
      if (!header) return;

      ensurePanelReaderTitle(header, content);
    });

    const containers = Array.from(root.querySelectorAll<HTMLElement>('.erom-accordion-container'));
    if (containers.length === 0) {
      syncAccordionContainer(root);
      return;
    }

    containers.forEach(container => syncAccordionContainer(container));
  }, [ensurePanelReaderTitle, syncAccordionContainer]);

  const handleClickCapture = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      const headerButton = (event.target as HTMLElement).closest(
        'button.erom-accordion-item-header'
      ) as HTMLButtonElement | null;
      event.stopPropagation();
      if (headerButton) {
        const parent = headerButton.closest('.erom-accordion-container') || document;
        const isExpanded = headerButton.getAttribute('aria-expanded') === 'true';
        syncAccordionContainer(parent, isExpanded ? null : headerButton);
      }
    },
    [syncAccordionContainer]
  );

  return (
    <div
      ref={ref}
      data-enhanced={isEnhanced ? 'true' : undefined}
      onClickCapture={handleClickCapture}
      className={cn(styles.accordion, className)}
      {...rest}
    />
  );
};

export default Accordion;
